import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { glassCardSx } from "src/theme/glassDialog";
import CloseIcon from "@mui/icons-material/Close";
import QuoteStepper from "./QuoteStepper";
import QuoteNavButtons from "./QuoteNavButtons";
import QuoteResumeNotice from "./QuoteResumeNotice";
import StepAddress from "./StepAddress";
import StepConcrete from "./StepConcrete";
import StepPump from "./StepPump";
import StepDetails from "./StepDetails";
import StepReview from "./StepReview";
import { useT } from "src/i18n";

const STEP_COMPONENTS = [StepAddress, StepPump, StepConcrete, StepDetails, StepReview];
const TOTAL = 5;
// Lisätiedot step — hosts the attachments UI. Derived from the array so it can't
// drift if steps are reordered.
const DETAILS_STEP = STEP_COMPONENTS.indexOf(StepDetails);
const PUMP_STEP = STEP_COMPONENTS.indexOf(StepPump);

export default function QuoteWizard({
    activeStep, maxReachedStep, draft, errors,
    resumeNotice, onStartOver, onDismissResume,
    submitting,
    asiakases, selectedAsiakasId,
    canAdvance, nextPending, nextBlockedReason,
    addressCheckStatus, addressCheckError, boomCheck, onAddressRetry, onAddressPicked,
    onChange, onJumpTo, onBack, onNext, onSubmit, onSelectAsiakas, onCreateCompany,
    onEditPerson, onEditSelectedAsiakas, onLaskutusEditingChange,
    onClose,
    isAuthenticated, person, onOpenLogin, onOpenRegister,
    attachments, pumppuRequestId, onRefetchAttachments, onPreClaimDraft,
    onAttachmentsUploadingChange,
    consentCopy,
}) {
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down(820));
    const t = useT();
    const StepComp = STEP_COMPONENTS[activeStep];
    const isFinal = activeStep === TOTAL - 1;
    // Step 1's primary button first runs the address feasibility check, then
    // advances — make that mode explicit instead of a silent double-press.
    const nextLabel = activeStep === 0 && !draft.feasibility ? t("wizard.quoteWizard.checkAddressNext") : t("wizard.navButtons.next");
    const w = theme.custom?.wizard ?? {};
    const easing = w.motion?.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)";
    // Step transitions slide in the direction of travel (forward = in from the
    // right, back = in from the left) so the wizard has a spatial memory instead
    // of a directionless crossfade. React's "adjust state during render" pattern
    // records the new step + direction synchronously before paint, so the keyed
    // step Box below mounts already knowing which way to slide — no ref read in
    // render. A StepDotList jump to an earlier step reads as "back", which is right.
    const [prevStep, setPrevStep] = useState(activeStep);
    const [stepDir, setStepDir] = useState(1);
    if (activeStep !== prevStep) {
        setStepDir(activeStep > prevStep ? 1 : -1);
        setPrevStep(activeStep);
    }

    // Animate the content area's height between steps so a tall→short jump
    // (e.g. Tarkista → Osoite) eases shut instead of snapping. FLIP via the Web
    // Animations API: the container rests at auto (content-driven), and on each
    // step change we animate it from the PREVIOUS measured height to the new one.
    // WAAPI is used (not a CSS height transition on a React-state value) because
    // emotion emits height as a class and the keyed remount collapses the
    // old→new change into one commit, leaving a CSS transition no start frame.
    const containerRef = useRef(null);
    const stepRef = useRef(null);
    const prevHRef = useRef(null);
    useEffect(() => {
        const container = containerRef.current;
        const el = stepRef.current;
        if (!container || !el) return;
        const newH = el.offsetHeight;
        const prevH = prevHRef.current;
        prevHRef.current = newH;
        const reduce = typeof window !== "undefined"
            && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (prevH != null && prevH !== newH && !reduce && container.animate) {
            container.animate(
                [{ height: `${prevH}px` }, { height: `${newH}px` }],
                { duration: 360, easing },
            );
        }
    }, [activeStep, easing]);

    return (
        <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 3, md: 6 } }}>
            {resumeNotice && (
                <QuoteResumeNotice
                    address={resumeNotice.address}
                    at={resumeNotice.at}
                    onStartOver={onStartOver}
                    onDismiss={onDismissResume}
                />
            )}
            <Box
                sx={{
                    position: "relative",
                    // The wizard card arrives once when the flow opens — a quiet
                    // rise, like the work-lamp coming on. Plays on mount only
                    // (this Box isn't keyed); the inner step Box owns per-step motion.
                    animation: `bjCardIn 300ms ${easing}`,
                    "@keyframes bjCardIn": {
                        from: { opacity: 0, transform: "translateY(6px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                    display: "grid",
                    gridTemplateColumns: isCompact ? "minmax(0, 1fr)" : "220px minmax(0, 1fr)",
                    columnGap: 5,
                    rowGap: 0,
                    padding: { xs: "28px 24px", md: "48px 56px" },
                    ...glassCardSx(theme),
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0, left: 0, right: 0,
                        height: 80,
                        background: "linear-gradient(180deg, rgba(244,242,238,0.10) 0%, transparent 100%)",
                        pointerEvents: "none",
                        zIndex: 0,
                    },
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        boxShadow: "inset 0 0 0 1px rgba(244,242,238,0.08)",
                        pointerEvents: "none",
                        zIndex: 2,
                    },
                }}
            >
                {onClose && (
                    <Tooltip title={t("common.dialog.close")}>
                        <IconButton
                            onClick={onClose}
                            aria-label={t("common.dialog.close")}
                            size="small"
                            sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                zIndex: 3,
                                color: "text.secondary",
                                "&:hover": { color: "text.primary", backgroundColor: "rgba(244,242,238,0.08)" },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Box sx={{ position: "relative", zIndex: 1 }}>
                    <QuoteStepper activeStep={activeStep} maxReachedStep={maxReachedStep} onJumpTo={onJumpTo} />
                </Box>
                <Box sx={{ position: "relative", zIndex: 1, minHeight: 360, display: "flex", flexDirection: "column" }}>
                    <Box
                        ref={containerRef}
                        sx={{
                        // No flex:1 — a flex-grown child is sized by the flex algorithm,
                        // which overrides the height we animate. Content-height + minHeight
                        // keeps the WAAPI height morph authoritative.
                        minHeight: 320,
                        // Clip during the height morph so an expanding step can't
                        // overlap the nav buttons mid-transition. The negative
                        // margin + matching padding keep the clip boundary ~10px
                        // outside the field edges so the amber focus-glow ring isn't
                        // shaved off (content position is unchanged — margin cancels padding).
                        overflow: "hidden",
                        mx: "-10px",
                        px: "10px",
                    }}>
                        <Box
                            ref={stepRef}
                            key={activeStep}
                            style={{ "--bj-enter-x": `${stepDir * 18}px` }}
                            sx={{
                                // Keyed remount per step → an enter-only animation is
                                // enough (the outgoing step unmounts instantly). The
                                // start offset's sign follows the travel direction, so
                                // forward slides in from the right, back from the left.
                                animation: `bjStepIn 260ms ${easing}`,
                                "@keyframes bjStepIn": {
                                    from: { opacity: 0, transform: "translateX(var(--bj-enter-x))" },
                                    to: { opacity: 1, transform: "translateX(0)" },
                                },
                                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                            }}
                        >
                            {isFinal ? (
                                <StepReview
                                    draft={draft}
                                    errors={errors}
                                    asiakases={asiakases}
                                    selectedAsiakasId={selectedAsiakasId}
                                    onSelectAsiakas={onSelectAsiakas}
                                    onCreateCompany={onCreateCompany}
                                    onJumpToStep={onJumpTo}
                                    onChange={onChange}
                                    onEditPerson={onEditPerson}
                                    onEditSelectedAsiakas={onEditSelectedAsiakas}
                                    onLaskutusEditingChange={onLaskutusEditingChange}
                                    isAuthenticated={isAuthenticated}
                                    person={person}
                                    attachments={attachments}
                                    consentCopy={consentCopy}
                                />
                            ) : activeStep === 0 ? (
                                <StepComp
                                    draft={draft}
                                    errors={errors}
                                    onChange={onChange}
                                    checkStatus={addressCheckStatus}
                                    checkError={addressCheckError}
                                    onRetry={onAddressRetry}
                                    onAddressPicked={onAddressPicked}
                                />
                            ) : activeStep === PUMP_STEP ? (
                                <StepComp
                                    draft={draft}
                                    errors={errors}
                                    onChange={onChange}
                                    boomCheck={boomCheck}
                                />
                            ) : activeStep === DETAILS_STEP ? (
                                <StepComp
                                    draft={draft}
                                    errors={errors}
                                    onChange={onChange}
                                    isAuthenticated={isAuthenticated}
                                    person={person}
                                    attachments={attachments}
                                    pumppuRequestId={pumppuRequestId}
                                    onRefetchAttachments={onRefetchAttachments}
                                    onPreClaimDraft={onPreClaimDraft}
                                    onUploadingChange={onAttachmentsUploadingChange}
                                />
                            ) : (
                                <StepComp draft={draft} errors={errors} onChange={onChange} />
                            )}
                        </Box>
                    </Box>
                    <QuoteNavButtons
                        activeStep={activeStep}
                        canAdvance={canAdvance}
                        isFinal={isFinal}
                        submitting={submitting}
                        nextPending={nextPending}
                        onBack={onBack}
                        onNext={onNext}
                        onSubmit={onSubmit}
                        nextLabel={nextLabel}
                        blockedReason={nextBlockedReason}
                        isAuthenticated={isAuthenticated}
                        onOpenLogin={onOpenLogin}
                        onOpenRegister={onOpenRegister}
                    />
                </Box>
            </Box>
            <Box sx={{
                mt: 3,
                px: 1,
                fontFamily: "'Inter', sans-serif",
                fontSize: 12,
                color: "text.secondary",
                letterSpacing: "0.02em",
                lineHeight: 1.6,
            }}>
                {isAuthenticated
                    ? t("wizard.quoteWizard.autosaveAuth")
                    : t("wizard.quoteWizard.autosaveAnon")}
            </Box>
        </Box>
    );
}
